import { useQuery } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { StaffPerfRow } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { ReportsExportSlot } from './ReportsExportSlot'
import { ExportButton } from './ExportButton'
import { formatDateRangeHuman } from '@/lib/utils'

interface Props {
  range: UseReportRange
}

/**
 * Staff / Server performance — per-server sales productivity plus two control
 * signals (discounts given, cancelled orders). Attribution is orders.user_id;
 * unattributed (system/online) sales roll up under "Unattributed". Business-day
 * keyed, sorted by net sales.
 */
export function StaffPerformanceTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const query = useQuery({
    queryKey: ['reports-v2', 'staff', range.fromISO, range.toISO],
    queryFn: () => apiClient.getStaffPerformance(range.fromISO, range.toISO),
    staleTime: 30_000,
  })
  const rows = query.data?.data?.rows ?? []
  const totals = rows.reduce(
    (acc, r) => ({
      orders: acc.orders + r.orders,
      covers: acc.covers + r.covers,
      gross: acc.gross + r.gross,
      net: acc.net + r.net,
      discounts: acc.discounts + r.discounts,
      serviceCharge: acc.serviceCharge + (r.service_charge ?? 0),
      cancelledOrders: acc.cancelledOrders + r.cancelled_orders,
      cancelledValue: acc.cancelledValue + r.cancelled_value,
    }),
    {
      orders: 0,
      covers: 0,
      gross: 0,
      net: 0,
      discounts: 0,
      serviceCharge: 0,
      cancelledOrders: 0,
      cancelledValue: 0,
    },
  )

  return (
    <div className="space-y-4">
      <ReportsExportSlot>
        <ExportButton
          report="staff"
          reportLabel="Staff performance"
          fromISO={range.fromISO}
          toISO={range.toISO}
        />
      </ReportsExportSlot>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Staff Performance — {formatDateRangeHuman(range.from, range.to)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {query.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No sales attributed to staff in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5">Server</th>
                    <th className="text-right px-4 py-2.5">Orders</th>
                    <th className="text-right px-4 py-2.5">Covers</th>
                    <th className="text-right px-4 py-2.5">Gross</th>
                    <th className="text-right px-4 py-2.5">Net</th>
                    <th className="text-right px-4 py-2.5">Avg Check</th>
                    <th className="text-right px-4 py-2.5">Discounts</th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="Share of collected service charges (distributed to staff at day close); excludes fully credit-noted orders"
                    >
                      Service Charge
                    </th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="Orders cancelled (a control signal) — count · value"
                    >
                      Cancelled
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r: StaffPerfRow) => (
                    <tr key={r.server} className="hover:bg-muted/40">
                      <td className="px-4 py-3 font-medium">{r.server}</td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {r.orders.toLocaleString('en-US')}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {r.covers.toLocaleString('en-US')}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">{formatCurrency(r.gross)}</td>
                      <td className="text-right tabular-nums px-4 py-3 font-semibold">
                        {formatCurrency(r.net)}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">{formatCurrency(r.avg_check)}</td>
                      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">
                        {r.discounts > 0 ? formatCurrency(r.discounts) : '—'}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {(r.service_charge ?? 0) > 0 ? formatCurrency(r.service_charge) : '—'}
                      </td>
                      <td className="text-right tabular-nums px-4 py-3">
                        {r.cancelled_orders > 0 ? (
                          <span className="text-amber-600 dark:text-amber-400">
                            {r.cancelled_orders} · {formatCurrency(r.cancelled_value)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/30 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {totals.orders.toLocaleString('en-US')}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {totals.covers.toLocaleString('en-US')}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.gross)}</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.net)}</td>
                    <td className="text-right tabular-nums px-4 py-3">—</td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.discounts)}</td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {formatCurrency(totals.serviceCharge)}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {totals.cancelledOrders} · {formatCurrency(totals.cancelledValue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
